import { z } from 'zod';
import type { Step } from '@/components/common';

/**
 * Unified Quote Wizard — shared step-list contract + the Motor branch schema.
 *
 * QuoteWizardPage picks `MOTOR_STEPS | LIFE_STEPS | HEALTH_STEPS` based on
 * `?type=`. Only the motor branch is fully built out here (Task 2.5); life
 * and health are single-step "coming soon" placeholders so Tasks 2.6/2.7 can
 * drop their real step arrays in without touching the container.
 */

// Indian RC (vehicle registration) format, e.g. "MH01AB1234", "MH-01-AB-1234",
// "DL 1C 1234" (2-digit RTO serial optional 3rd letter block, 1-4 digit serial).
/**
 * B9 -- the renewed policy's id, on every branch. A hidden field: it is only
 * ever written from a server-provided id, so the uuid check guards the wire,
 * not the user.
 */
export const previousPolicyIdField = z.string().uuid().optional().or(z.literal(''));

export const RC_REGEX =/^[A-Z]{2}[ -]?\d{1,2}[ -]?[A-Z]{0,3}[ -]?\d{1,4}$/;

// Indian PIN code. Declared here rather than beside the life/health schemas
// that also use it, because the MOTOR schema below is evaluated first and a
// `const` referenced before its declaration is a TDZ crash at import time.
export const PIN_CODE_REGEX = /^\d{6}$/;

/**
 * A stored `Customer.pincode` as this form can use it.
 *
 * That column is free text typed by broker staff and nothing validates it, so
 * '411 001' and '41100' both exist. Digits are kept, because a PIN with a
 * space in it is still that PIN; anything that is then not six digits returns
 * '' rather than a truncated guess, leaving the required field empty and
 * visibly unanswered instead of prefilled with a number no one chose.
 */
export function normalizeStoredPincode(raw: string | null | undefined): string {
  const digits = (raw || '').replace(/\D/g, '');
  return PIN_CODE_REGEX.test(digits) ? digits : '';
}

export const VEHICLE_CATEGORY_OPTIONS = [
  { value: 'two_wheeler', label: 'Two Wheeler' },
  { value: 'private_car', label: 'Private Car' },
  { value: 'commercial_vehicle', label: 'Commercial Vehicle' },
] as const;

export const BUSINESS_TYPE_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'rollover', label: 'Rollover (used vehicle)' },
] as const;

// Standard Indian motor NCB (No Claim Bonus) slabs.
export const PREVIOUS_NCB_OPTIONS = [
  { value: '0', label: '0%' },
  { value: '20', label: '20%' },
  { value: '25', label: '25%' },
  { value: '35', label: '35%' },
  { value: '45', label: '45%' },
  { value: '50', label: '50%' },
];

// comprehensive / tp_only / saod are the exact suffixes the GoDigit adapter's
// product-code table keys off of (see backend/tests/integrations/
// test_godigit_e2e.py::test_two_wheeler_product_codes) and match the
// `product_type` convention the QuoteSubmitSerializer test suite exercises
// (e.g. "motor_comprehensive", "motor_saod").
export const PRODUCT_VARIANT_OPTIONS = [
  { value: 'comprehensive', label: 'Comprehensive' },
  { value: 'tp_only', label: 'Third Party Only' },
  { value: 'saod', label: 'Standalone Own Damage (SAOD)' },
] as const;

const CURRENT_YEAR = new Date().getFullYear();

function startOfTodayCeiling(): Date {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

export const motorQuoteSchema = z
  .object({
    // Customer step (Wave F1 item 4b): the wizard used to derive the
    // proposer silently from ?customer=<id> (deep links only) or, failing
    // that, the LOGGED-IN broker employee's own profile — so a fresh
    // "New Quote" from /quotes/new used the employee's own identity with no
    // way to pick who the quote is actually for. `customer_id` is now a
    // real, gated form field: CustomerStep sets it (and the identity
    // snapshot fields below) either by selecting an existing customer or by
    // creating a new one inline, and Next stays disabled until it's set.
    customer_id: z.string().min(1, 'Select or add a customer to continue'),
    customer_name: z.string().optional(),
    customer_phone: z.string().optional(),
    customer_email: z.string().optional(),
    customer_dob: z.string().optional(),
    customer_gender: z.string().optional(),
    // B-02/C1. The proposer's PIN, snapshotted from the chosen customer the
    // same way the identity fields above are. It was never on this form at
    // all: `onMotorSubmit` read `customer?.pincode`, and that `customer` comes
    // from `?customer=<id>`, so a quote started normally sent no pincode --
    // and GoDigit's payload builder REFUSES without one, because motor is zone
    // rated on it. The server falls back to the linked Customer's own pincode,
    // which covers most of it; 59 of 408 live customers have a blank one, and
    // the "Add New Customer" branch of this very step does not ask for one, so
    // for those the whole wizard ended in a refusal from our own code. Asked
    // here, prefilled, so it is a confirmation rather than a question.
    customer_pincode: z
      .string()
      .min(1, 'PIN code is required')
      .regex(PIN_CODE_REGEX, 'PIN code must be 6 digits'),
    vehicle_category: z.enum(['two_wheeler', 'private_car', 'commercial_vehicle'], {
      required_error: 'Vehicle type is required',
    }),
    business_type: z.enum(['new', 'renewal', 'rollover'], {
      required_error: 'Business type is required',
    }),
    product_variant: z.enum(['comprehensive', 'tp_only', 'saod'], {
      required_error: 'Product variant is required',
    }),
    make_id: z.string().min(1, 'Make is required'),
    make_name: z.string().optional(),
    model_id: z.string().min(1, 'Model is required'),
    model_name: z.string().optional(),
    variant_id: z.string().min(1, 'Variant is required'),
    variant_name: z.string().optional(),
    fuel_type: z.string().optional(),
    registration_pending: z.boolean().optional(),
    registration_number: z.string().optional(),
    registration_date: z.string().min(1, 'Registration date is required'),
    manufacturing_year: z.string().min(1, 'Manufacturing year is required'),
    rto_code: z.string().min(1, 'RTO is required'),
    rto_label: z.string().optional(),

    // Prior Policy (Wave F2 item 3a) — collected only for Renewal/Rollover;
    // the PriorPolicyStep is skipped entirely for New business (see
    // motorStepsForBusinessType). previous_insurer_id is the real FK the
    // backend resolves onto QuoteRequest.previous_insurer; _name is display
    // only. Wired to backend/apps/quotes/models.py QuoteRequest.previous_*.
    previous_policy_number: z.string().optional(),
    previous_insurer_id: z.string().optional(),
    previous_insurer_name: z.string().optional(),
    previous_policy_expiry: z.string().optional(),
    previous_ncb: z.string().optional(),
    has_claim_in_previous: z.enum(['yes', 'no']).optional(),

    // B9 -- the policy this quote renews, carried to the submit as the
    // top-level `previous_policy_id`. Set from `?renewal=` or from the
    // vehicle duplicate prompt; never typed.
    previous_policy_id: previousPolicyIdField,

    // B-04 -- the registration lookup's engine and chassis numbers, carried
    // (never typed here) to the submit's `vehicle` block, from which the
    // quote -> policy conversion writes them onto the policy's vehicle.
    engine_number: z.string().optional(),
    chassis_number: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const year = Number(data.manufacturing_year);
    const yearValid = data.manufacturing_year !== '' && Number.isFinite(year) && year > 1900;

    // Manufacturing Year <= current year.
    if (data.manufacturing_year && yearValid && year > CURRENT_YEAR) {
      ctx.addIssue({
        path: ['manufacturing_year'],
        code: z.ZodIssueCode.custom,
        message: `Manufacturing year cannot be after ${CURRENT_YEAR}`,
      });
    }

    // Registration Date not in the future.
    if (data.registration_date) {
      const regDate = new Date(data.registration_date);
      if (!Number.isNaN(regDate.getTime()) && regDate.getTime() > startOfTodayCeiling().getTime()) {
        ctx.addIssue({
          path: ['registration_date'],
          code: z.ZodIssueCode.custom,
          message: 'Registration date cannot be in the future',
        });
      }

      // Registration Date >= Jan 1 of Manufacturing Year. Surfaced on the
      // Manufacturing Year field/step since that's entered after Registration
      // Date in the wizard order, so this is the point both values are known.
      if (yearValid && !Number.isNaN(regDate.getTime())) {
        const jan1OfManufacturingYear = new Date(`${data.manufacturing_year}-01-01T00:00:00`);
        if (regDate.getTime() < jan1OfManufacturingYear.getTime()) {
          ctx.addIssue({
            path: ['manufacturing_year'],
            code: z.ZodIssueCode.custom,
            message: `Registration date cannot be before Jan 1, ${data.manufacturing_year} (the manufacturing year)`,
          });
        }
      }
    }

    // Registration Number: always required for used vehicles (renewal/
    // rollover). For New business it's required UNLESS "Registration
    // Pending" is checked.
    const isNew = data.business_type === 'new';
    const registrationNumberRequired = isNew ? !data.registration_pending : true;
    const regNumber = (data.registration_number || '').trim();

    if (registrationNumberRequired && !regNumber) {
      ctx.addIssue({
        path: ['registration_number'],
        code: z.ZodIssueCode.custom,
        message: 'Registration number is required',
      });
    } else if (regNumber && !RC_REGEX.test(regNumber.toUpperCase())) {
      ctx.addIssue({
        path: ['registration_number'],
        code: z.ZodIssueCode.custom,
        message: 'Enter a valid registration number, e.g. MH01AB1234',
      });
    }

    // Prior Policy: required for Renewal/Rollover only — the PriorPolicyStep
    // never renders for New business (motorStepsForBusinessType skips it),
    // so these are only ever missing when they should be.
    if (data.business_type !== 'new') {
      if (!data.previous_policy_number?.trim()) {
        ctx.addIssue({
          path: ['previous_policy_number'],
          code: z.ZodIssueCode.custom,
          message: 'Previous policy number is required',
        });
      }
      if (!data.previous_insurer_id) {
        ctx.addIssue({
          path: ['previous_insurer_id'],
          code: z.ZodIssueCode.custom,
          message: 'Previous insurer is required',
        });
      }
      if (!data.previous_policy_expiry) {
        ctx.addIssue({
          path: ['previous_policy_expiry'],
          code: z.ZodIssueCode.custom,
          message: 'Previous policy expiry date is required',
        });
      }
    }
  });

export type MotorQuoteFormData = z.infer<typeof motorQuoteSchema>;

// Wave F2 (item 3) reordered Registration Number ahead of Product Variant/
// Make/Model/Variant — entering it early lets RegistrationNumberStep run an
// upfront lookup (item 3b) against our own issued policies/customer vehicles
// and prefill everything downstream instead of the user re-typing it all.
// Business Type stays BEFORE Registration Number (unchanged) so the existing
// registration_pending gating (only for New business) keeps working, and it
// also gates the new Prior Policy step right after it — shown only for
// Renewal/Rollover (item 3a), skipped for New via motorStepsForBusinessType.
/**
 * Shared step id for the multi-insurer premium options step. It sits directly
 * after Insurance Type on EVERY branch (motor/life/health here, and the
 * generic schema-driven flow in `genericSchema.ts`), gates nothing, and sends
 * nothing during the wizard — the rows are held as drafts and POSTed to
 * `/quotes/requests/{id}/options/` once the request exists. Leaving it empty
 * reproduces each branch's previous behaviour exactly, which is what keeps the
 * live GoDigit motor path unchanged.
 */
export const PREMIUM_OPTIONS_STEP_ID = 'premium-options';

/** The grouped Vehicle Details step — everything about the car itself. */
export const VEHICLE_DETAILS_STEP_ID = 'vehicle-details';
/** The grouped Product & Premium Selection step. */
export const PRODUCT_PREMIUM_STEP_ID = 'product-premium';

/**
 * Six steps, in the order the business asked for them.
 *
 * This used to be fifteen — one field per screen, with Premium Options sitting
 * second, before the wizard knew which vehicle it was pricing. The fields are
 * unchanged; what changed is how many times the broker presses Next. Related
 * fields now share a screen, which is also what lets Registration Number
 * prefill Make/Model/Variant/dates/RTO *in front of the user* instead of
 * silently populating screens they then walk through one at a time.
 *
 * Customer comes first because a quote is for somebody, and Business Type
 * second because it decides whether prior-policy details are wanted at all.
 */
export const MOTOR_STEPS: Step[] = [
  { id: 'customer', title: 'Customer Details' },
  { id: 'business-type', title: 'Business Type' },
  { id: 'insurance-type', title: 'Insurance Type' },
  { id: VEHICLE_DETAILS_STEP_ID, title: 'Vehicle Details' },
  { id: PRODUCT_PREMIUM_STEP_ID, title: 'Product & Premium Selection' },
  { id: 'review', title: 'Review & Submit' },
];

/**
 * The motor step list to actually render.
 *
 * Prior Policy used to be a step of its own, dropped for New business. It is
 * now a *section* inside Vehicle Details, so nothing is added or removed from
 * the step list any more — `motorStepFieldsFor` decides whether its fields are
 * required, and the section itself is hidden for New business. The signature
 * is kept because the container and the deep-link prefill both call it, and a
 * business type that changes mid-flow must still be able to re-derive the list.
 */
export function motorStepsForBusinessType(_businessType?: string): Step[] {
  return MOTOR_STEPS;
}

/** Prior-policy fields, required only for Renewal/Rollover. */
const PRIOR_POLICY_FIELDS: (keyof MotorQuoteFormData)[] = [
  'previous_policy_number',
  'previous_insurer_id',
  'previous_policy_expiry',
];

/**
 * Mandatory fields for a step, given the business type.
 *
 * Only Vehicle Details varies: it carries the prior-policy section, which is
 * wanted for Renewal/Rollover and must NOT be gated on for New business —
 * otherwise Next would be dead on a step whose prior-policy fields are hidden.
 */
export function motorStepFieldsFor(
  stepId: string | undefined,
  businessType?: string
): (keyof MotorQuoteFormData)[] {
  const base = MOTOR_STEP_FIELDS[stepId ?? ''] ?? [];
  if (stepId === VEHICLE_DETAILS_STEP_ID && businessType && businessType !== 'new') {
    return [...base, ...PRIOR_POLICY_FIELDS];
  }
  return base;
}

/**
 * The step that owns a motor field, for sending the broker back to it.
 *
 * B-02/C1 review: Review gates on no fields, so Submit is always enabled
 * there; a schema failure on a field belonging to a step the broker never SAW
 * (the lead deep link jumps straight to Business Type, skipping Customer) is
 * otherwise a click that does nothing. `QuoteWizardPage`'s invalid-handler
 * uses this to name the step rather than swallow the refusal.
 */
export function motorStepIdForField(
  field: keyof MotorQuoteFormData,
  businessType?: string
): string | undefined {
  if (PRIOR_POLICY_FIELDS.includes(field)) return VEHICLE_DETAILS_STEP_ID;
  return Object.keys(MOTOR_STEP_FIELDS).find((stepId) =>
    motorStepFieldsFor(stepId, businessType).includes(field)
  );
}

// Last-resort fallback for a `?type=` value that matches NO configured
// insurance type at all (e.g. a hand-edited URL). Every real type now gets a
// flow: motor/life/health from the hand-written branches here, everything else
// from the schema-driven generic flow in `genericSchema.ts`. Single "Insurance
// Type" step so the container has something valid to render and switching type
// never crashes.
export const PLACEHOLDER_STEPS: Step[] = [{ id: 'insurance-type', title: 'Insurance Type' }];

// Mandatory fields per motor step, used to gate Next via `trigger(stepFields)`.
// Read through `motorStepFieldsFor`, which layers the business-type-dependent
// prior-policy fields on top.
export const MOTOR_STEP_FIELDS: Record<string, (keyof MotorQuoteFormData)[]> = {
  customer: ['customer_id', 'customer_pincode'],
  'business-type': ['business_type'],
  'insurance-type': [],
  [VEHICLE_DETAILS_STEP_ID]: [
    'vehicle_category',
    'registration_number',
    'make_id',
    'model_id',
    'variant_id',
    'registration_date',
    'manufacturing_year',
    'rto_code',
  ],
  // Premium options live outside the react-hook-form instance (they carry a
  // File, and there is nothing to attach them to until the request exists), so
  // only the product variant is triggerable here. QuoteWizardPage adds the
  // row-level premium-option gate on top.
  [PRODUCT_PREMIUM_STEP_ID]: ['product_variant'],
  review: [],
};

export const MOTOR_DEFAULT_VALUES: Partial<MotorQuoteFormData> = {
  business_type: 'new',
  registration_pending: false,
  // Declared so the PIN input is controlled from the first render rather than
  // flipping from uncontrolled once a customer is picked.
  customer_pincode: '',
};

// ---------------------------------------------------------------------------
// Life branch (Task 2.6)
// ---------------------------------------------------------------------------
// No insurer adapter exists for life yet (Task 2.4's "no_adapter" path), so
// submitting always returns HTTP 200 status="no_quotes_available" — rendered
// by QuoteWizardPage as an informational panel rather than routed to the
// quote-comparison screen the motor branch uses.

export const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

export const RESIDENCE_STATUS_OPTIONS = [
  { value: 'resident_indian', label: 'Resident Indian' },
  { value: 'nri', label: 'Non-Resident Indian (NRI)' },
  { value: 'foreign_national', label: 'Foreign National' },
];

export const PREMIUM_PAYMENT_FREQUENCY_OPTIONS = [
  { value: 'yearly', label: 'Yearly' },
  { value: 'half_yearly', label: 'Half-Yearly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'single', label: 'Single Premium' },
];

export const PROPOSER_RELATIONSHIP_OPTIONS = [
  { value: 'self', label: 'Self' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'son', label: 'Son' },
  { value: 'daughter', label: 'Daughter' },
  { value: 'father', label: 'Father' },
  { value: 'mother', label: 'Mother' },
  { value: 'other', label: 'Other' },
];

export const PLAN_VARIANT_OPTIONS = [
  { value: 'term_plan', label: 'Term Plan' },
  { value: 'endowment', label: 'Endowment Plan' },
  { value: 'whole_life', label: 'Whole Life Plan' },
  { value: 'money_back', label: 'Money Back Plan' },
  { value: 'ulip', label: 'ULIP' },
  { value: 'pension', label: 'Pension Plan' },
  { value: 'child_plan', label: 'Child Plan' },
];

export const LIFE_BENEFIT_OPTION_OPTIONS = [
  { value: 'level_cover', label: 'Level Cover' },
  { value: 'increasing_cover', label: 'Increasing Cover' },
  { value: 'decreasing_cover', label: 'Decreasing Cover' },
  { value: 'return_of_premium', label: 'Return of Premium' },
];

export const MEDICAL_STATUS_OPTIONS = [
  { value: 'medical', label: 'Medical' },
  { value: 'non_medical', label: 'Non-Medical' },
];

export const EMPLOYMENT_TYPE_OPTIONS = [
  { value: 'salaried', label: 'Salaried' },
  { value: 'self_employed', label: 'Self-Employed' },
  { value: 'business', label: 'Business' },
  { value: 'professional', label: 'Professional' },
  { value: 'homemaker', label: 'Homemaker' },
  { value: 'student', label: 'Student' },
  { value: 'retired', label: 'Retired' },
  { value: 'unemployed', label: 'Unemployed' },
  { value: 'other', label: 'Other' },
];

export const EDUCATION_QUALIFICATION_OPTIONS = [
  { value: 'below_10th', label: 'Below 10th' },
  { value: '10th', label: '10th' },
  { value: '12th', label: '12th' },
  { value: 'graduate', label: 'Graduate' },
  { value: 'post_graduate', label: 'Post Graduate' },
  { value: 'doctorate', label: 'Doctorate' },
  { value: 'professional', label: 'Professional' },
  { value: 'other', label: 'Other' },
];


/** Whole-years age as of today, or undefined if `dob` is empty/unparseable. */
export function calculateAge(dob: string | undefined): number | undefined {
  if (!dob) return undefined;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return undefined;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : undefined;
}

export const lifeQuoteSchema = z
  .object({
    // Proposer identity — captured here (rather than reused silently from the
    // linked customer/user like the motor branch does) because Life needs the
    // name split into first/middle/last for the insurer proposal.
    first_name: z.string().min(1, 'First name is required'),
    middle_name: z.string().optional(),
    last_name: z.string().min(1, 'Last name is required'),
    gender: z.enum(['male', 'female', 'other'], { required_error: 'Gender is required' }),
    date_of_birth: z.string().min(1, 'Date of birth is required'),
    age: z.number().optional(),

    // Life Assured — the person whose life is covered. May differ from the
    // proposer (e.g. a parent buying cover for a child).
    life_assured_same_as_proposer: z.enum(['yes', 'no']).default('yes'),
    life_assured_first_name: z.string().optional(),
    life_assured_middle_name: z.string().optional(),
    life_assured_last_name: z.string().optional(),
    life_assured_gender: z.enum(['male', 'female', 'other']).optional(),
    life_assured_date_of_birth: z.string().optional(),
    life_assured_age: z.number().optional(),

    residence_status: z.string().min(1, 'Residence status is required'),
    pincode: z
      .string()
      .min(1, 'PIN code is required')
      .regex(PIN_CODE_REGEX, 'PIN code must be 6 digits'),
    proposer_state_id: z.string().min(1, 'Proposer state is required'),
    proposer_state_name: z.string().optional(),
    sales_person_state_id: z.string().min(1, 'Sales person state is required'),
    sales_person_state_name: z.string().optional(),

    // Product details
    policy_term_years: z.string().min(1, 'Policy term is required'),
    premium_payment_term_years: z.string().min(1, 'Premium payment term is required'),
    premium_payment_frequency: z.string().min(1, 'Premium payment frequency is required'),
    existing_customer: z.enum(['yes', 'no'], { required_error: 'Existing customer is required' }),
    first_time_buyer: z.enum(['yes', 'no'], { required_error: 'First time buyer is required' }),
    premium_holiday: z.enum(['yes', 'no'], { required_error: 'Premium holiday is required' }),
    proposer_relationship: z.string().min(1, 'Proposer relationship is required'),
    plan_variant: z.string().min(1, 'Plan variant is required'),
    life_benefit_option: z.string().min(1, 'Life benefit option is required'),
    medical_non_medical: z.string().min(1, 'Medical / Non-Medical is required'),
    smoking_tobacco_usage: z.enum(['yes', 'no'], {
      required_error: 'Smoking/Tobacco usage is required',
    }),
    occupation: z.string().min(1, 'Occupation is required'),
    employment_type: z.string().min(1, 'Employment type is required'),
    education_qualification: z.string().min(1, 'Education qualification is required'),
    annual_income: z.string().min(1, 'Annual income is required'),
    sum_assured: z.string().min(1, 'Sum assured is required'),
    // B9 -- see previousPolicyIdField.
    previous_policy_id: previousPolicyIdField,
  })
  .superRefine((data, ctx) => {
    // Proposer DOB: not in the future, and old enough to own a policy (18+).
    if (data.date_of_birth) {
      const dob = new Date(data.date_of_birth);
      if (!Number.isNaN(dob.getTime())) {
        if (dob.getTime() > startOfTodayCeiling().getTime()) {
          ctx.addIssue({
            path: ['date_of_birth'],
            code: z.ZodIssueCode.custom,
            message: 'Date of birth cannot be in the future',
          });
        } else if ((calculateAge(data.date_of_birth) ?? 0) < 18) {
          ctx.addIssue({
            path: ['date_of_birth'],
            code: z.ZodIssueCode.custom,
            message: 'Proposer must be at least 18 years old',
          });
        }
      }
    }

    // Life Assured sub-section is only mandatory when it's NOT the proposer.
    if (data.life_assured_same_as_proposer === 'no') {
      if (!data.life_assured_first_name?.trim()) {
        ctx.addIssue({
          path: ['life_assured_first_name'],
          code: z.ZodIssueCode.custom,
          message: 'Life Assured first name is required',
        });
      }
      if (!data.life_assured_last_name?.trim()) {
        ctx.addIssue({
          path: ['life_assured_last_name'],
          code: z.ZodIssueCode.custom,
          message: 'Life Assured last name is required',
        });
      }
      if (!data.life_assured_gender) {
        ctx.addIssue({
          path: ['life_assured_gender'],
          code: z.ZodIssueCode.custom,
          message: 'Life Assured gender is required',
        });
      }
      if (!data.life_assured_date_of_birth) {
        ctx.addIssue({
          path: ['life_assured_date_of_birth'],
          code: z.ZodIssueCode.custom,
          message: 'Life Assured date of birth is required',
        });
      } else {
        const laDob = new Date(data.life_assured_date_of_birth);
        if (!Number.isNaN(laDob.getTime()) && laDob.getTime() > startOfTodayCeiling().getTime()) {
          ctx.addIssue({
            path: ['life_assured_date_of_birth'],
            code: z.ZodIssueCode.custom,
            message: 'Date of birth cannot be in the future',
          });
        }
      }
    }

    // Premium Payment Term cannot exceed the Policy Term.
    const term = Number(data.policy_term_years);
    const pptTerm = Number(data.premium_payment_term_years);
    if (
      data.policy_term_years &&
      data.premium_payment_term_years &&
      Number.isFinite(term) &&
      Number.isFinite(pptTerm) &&
      pptTerm > term
    ) {
      ctx.addIssue({
        path: ['premium_payment_term_years'],
        code: z.ZodIssueCode.custom,
        message: 'Premium payment term cannot exceed the policy term',
      });
    }

    // Annual Income / Sum Assured must be positive numbers.
    const income = Number(data.annual_income);
    if (data.annual_income && (!Number.isFinite(income) || income <= 0)) {
      ctx.addIssue({
        path: ['annual_income'],
        code: z.ZodIssueCode.custom,
        message: 'Enter a valid annual income',
      });
    }
    const sumAssured = Number(data.sum_assured);
    if (data.sum_assured && (!Number.isFinite(sumAssured) || sumAssured <= 0)) {
      ctx.addIssue({
        path: ['sum_assured'],
        code: z.ZodIssueCode.custom,
        message: 'Enter a valid sum assured',
      });
    }
  });

export type LifeQuoteFormData = z.infer<typeof lifeQuoteSchema>;

export const LIFE_STEPS: Step[] = [
  { id: 'insurance-type', title: 'Insurance Type' },
  { id: PREMIUM_OPTIONS_STEP_ID, title: 'Premium Options' },
  { id: 'basic-details', title: 'Basic Details' },
  { id: 'product-details', title: 'Product Details' },
  { id: 'review', title: 'Review & Submit' },
];

// Mandatory fields per life step, used to gate Next via `trigger(stepFields)`.
// Life Assured fields are listed unconditionally — they're `.optional()` in
// the schema and only enforced by superRefine when the "same as proposer"
// toggle is "no", so triggering them when "yes" is always a no-op pass.
export const LIFE_STEP_FIELDS: Record<string, (keyof LifeQuoteFormData)[]> = {
  'insurance-type': [],
  [PREMIUM_OPTIONS_STEP_ID]: [],
  'basic-details': [
    'first_name',
    'last_name',
    'gender',
    'date_of_birth',
    'life_assured_same_as_proposer',
    'life_assured_first_name',
    'life_assured_last_name',
    'life_assured_gender',
    'life_assured_date_of_birth',
    'residence_status',
    'pincode',
    'proposer_state_id',
    'sales_person_state_id',
  ],
  'product-details': [
    'policy_term_years',
    'premium_payment_term_years',
    'premium_payment_frequency',
    'existing_customer',
    'first_time_buyer',
    'premium_holiday',
    'proposer_relationship',
    'plan_variant',
    'life_benefit_option',
    'medical_non_medical',
    'smoking_tobacco_usage',
    'occupation',
    'employment_type',
    'education_qualification',
    'annual_income',
    'sum_assured',
  ],
  review: [],
};

export const LIFE_DEFAULT_VALUES: Partial<LifeQuoteFormData> = {
  life_assured_same_as_proposer: 'yes',
};

// ---------------------------------------------------------------------------
// Health branch (Task 2.7)
// ---------------------------------------------------------------------------
// No insurer adapter exists for health either (Task 2.4's "no_adapter" path),
// so submitting always returns HTTP 200 status="no_quotes_available" —
// rendered the same way as the Life branch's informational panel. Health has
// no dedicated proposer-identity step (unlike Life); it reuses the linked
// customer/user's identity silently, the same way Motor does.

export const HEALTH_BUSINESS_TYPE_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'portability', label: 'Portability' },
] as const;

export const ADULTS_OPTIONS = Array.from({ length: 4 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

export const CHILDREN_OPTIONS = Array.from({ length: 5 }, (_, i) => ({
  value: String(i),
  label: String(i),
}));

export const HEALTH_MEMBER_RELATIONSHIP_OPTIONS = [
  { value: 'self', label: 'Self' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'son', label: 'Son' },
  { value: 'daughter', label: 'Daughter' },
  { value: 'father', label: 'Father' },
  { value: 'mother', label: 'Mother' },
  { value: 'father_in_law', label: 'Father-in-law' },
  { value: 'mother_in_law', label: 'Mother-in-law' },
  { value: 'brother', label: 'Brother' },
  { value: 'sister', label: 'Sister' },
  { value: 'other', label: 'Other' },
];

export const HEALTH_POLICY_TENURE_OPTIONS = Array.from({ length: 5 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1} year${i === 0 ? '' : 's'}`,
}));

// Values are the plain rupee amount the backend's sum_insured DecimalField
// stores. The top tier ("₹1 Crore – Unlimited") sends the ₹1 Crore floor —
// there's no numeric way to represent "Unlimited" in a DecimalField, so the
// open-endedness lives in the label only.
export const HEALTH_SUM_INSURED_OPTIONS = [
  { value: '750000', label: '₹7.5 Lakh' },
  { value: '1000000', label: '₹10 Lakh' },
  { value: '1500000', label: '₹15 Lakh' },
  { value: '2000000', label: '₹20 Lakh' },
  { value: '2500000', label: '₹25 Lakh' },
  { value: '5000000', label: '₹50 Lakh' },
  { value: '10000000', label: '₹1 Crore – Unlimited' },
];

const healthMemberSchema = z.object({
  // Field names match HealthMemberSerializer (backend/apps/integrations/
  // serializers.py) exactly so the submit payload needs no translation.
  relation: z.string().min(1, 'Relationship is required'),
  date_of_birth: z.string().min(1, 'Date of birth is required'),
});

export type HealthMemberFormData = z.infer<typeof healthMemberSchema>;

// DOB-not-in-the-future check reused across both member arrays.
function checkMemberDobIssues(
  members: HealthMemberFormData[],
  arrayPath: 'adult_members' | 'child_members',
  ctx: z.RefinementCtx
): void {
  members.forEach((member, index) => {
    if (!member.date_of_birth) return;
    const dob = new Date(member.date_of_birth);
    if (!Number.isNaN(dob.getTime()) && dob.getTime() > startOfTodayCeiling().getTime()) {
      ctx.addIssue({
        path: [arrayPath, index, 'date_of_birth'],
        code: z.ZodIssueCode.custom,
        message: 'Date of birth cannot be in the future',
      });
    }
  });
}

export const healthQuoteSchema = z
  .object({
    business_type: z.enum(['new', 'portability'], {
      required_error: 'Business type is required',
    }),

    // PIN Code drives an on-blur lookup (ProductDetailsStep) that
    // auto-populates state/city below — both read-only, never
    // independently edited by the user.
    pincode: z
      .string()
      .min(1, 'PIN code is required')
      .regex(PIN_CODE_REGEX, 'PIN code must be 6 digits'),
    state: z.string().optional(),
    city: z.string().optional(),

    adults: z.string().min(1, 'Number of adults is required'),
    children: z.string().min(1, 'Number of children is required'),
    // Members are held in TWO role-keyed arrays (not one positional array):
    // each is grown/shrunk by its OWN count in InsuredDetailsStep, so a
    // member's relation/DOB always stays attached to the adult/child slot it
    // was entered under even when the other count changes. They're merged
    // into the single `members` payload array only at submit time
    // (QuoteWizardPage.onHealthSubmit) — the backend shape is unchanged.
    adult_members: z.array(healthMemberSchema),
    child_members: z.array(healthMemberSchema),

    policy_term_years: z.string().min(1, 'Policy tenure is required'),
    sum_insured: z.string().min(1, 'Sum insured is required'),

    applicant_annual_income: z.string().min(1, 'Applicant annual income is required'),
    pre_existing_condition: z.enum(['yes', 'no'], {
      required_error: 'Pre-existing medical condition is required',
    }),
    nri_discount: z.enum(['yes', 'no'], { required_error: 'NRI discount is required' }),
    bureau_score_discount: z.enum(['yes', 'no'], {
      required_error: 'Bureau score discount is required',
    }),
    // B9 -- see previousPolicyIdField.
    previous_policy_id: previousPolicyIdField,
  })
  .superRefine((data, ctx) => {
    // The PIN lookup must have actually resolved a state/city before the
    // request is usable — a syntactically valid but unrecognised PIN code
    // (or one the user never blurred out of) must block progress rather than
    // silently submit with no location.
    if (PIN_CODE_REGEX.test(data.pincode) && !data.state?.trim()) {
      ctx.addIssue({
        path: ['pincode'],
        code: z.ZodIssueCode.custom,
        message: 'Could not determine State/City for this PIN code — check and try again',
      });
    }

    // Each array's length must match its OWN count (InsuredDetailsStep keeps
    // them in lockstep via two useFieldArrays, so a mismatch means the sync
    // effect hasn't caught up yet — treat as not-yet-valid). At least one
    // adult is always required (Adults selector starts at 1).
    if (data.adult_members.length !== (Number(data.adults) || 0)) {
      ctx.addIssue({
        path: ['adult_members'],
        code: z.ZodIssueCode.custom,
        message: 'Adult details are still being prepared for the selected number of adults',
      });
    }
    if (data.child_members.length !== (Number(data.children) || 0)) {
      ctx.addIssue({
        path: ['child_members'],
        code: z.ZodIssueCode.custom,
        message: 'Child details are still being prepared for the selected number of children',
      });
    }

    // Each member's DOB cannot be in the future (both arrays).
    checkMemberDobIssues(data.adult_members, 'adult_members', ctx);
    checkMemberDobIssues(data.child_members, 'child_members', ctx);

    // Applicant Annual Income must be a positive number.
    const income = Number(data.applicant_annual_income);
    if (data.applicant_annual_income && (!Number.isFinite(income) || income <= 0)) {
      ctx.addIssue({
        path: ['applicant_annual_income'],
        code: z.ZodIssueCode.custom,
        message: 'Enter a valid annual income',
      });
    }
  });

export type HealthQuoteFormData = z.infer<typeof healthQuoteSchema>;

export const HEALTH_STEPS: Step[] = [
  { id: 'insurance-type', title: 'Insurance Type' },
  { id: PREMIUM_OPTIONS_STEP_ID, title: 'Premium Options' },
  { id: 'product-details', title: 'Product Details' },
  { id: 'insured-details', title: 'Insured Details' },
  { id: 'premium-details', title: 'Premium Details' },
  { id: 'additional-details', title: 'Additional Details' },
  { id: 'review', title: 'Review & Submit' },
];

// Mandatory fields per health step, used to gate Next via
// `trigger(stepFields)`. On the LAST step ("review"), QuoteWizardPage
// triggers every field listed across every step below (not just this
// step's own empty list) so "Calculate Premium" stays disabled until the
// ENTIRE flow is valid, per the brief.
export const HEALTH_STEP_FIELDS: Record<string, (keyof HealthQuoteFormData)[]> = {
  'insurance-type': [],
  // Empty, so the "trigger every field across the whole flow on review"
  // flattening below is unaffected by this step existing.
  [PREMIUM_OPTIONS_STEP_ID]: [],
  'product-details': ['business_type', 'pincode'],
  'insured-details': ['adults', 'children', 'adult_members', 'child_members'],
  'premium-details': ['policy_term_years', 'sum_insured'],
  'additional-details': [
    'applicant_annual_income',
    'pre_existing_condition',
    'nri_discount',
    'bureau_score_discount',
  ],
  review: [],
};

export const HEALTH_DEFAULT_VALUES: Partial<HealthQuoteFormData> = {
  adults: '1',
  children: '0',
  adult_members: [{ relation: '', date_of_birth: '' }],
  child_members: [],
};
